import * as React from 'react';

import { Label as ShadcnLabel } from '@/hen-ui/shadcn/label';
import { labelVariants, type LabelVariantProps } from './label.variants';
import { cn } from '@/hen-ui/utils';

export interface LabelProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof ShadcnLabel>,
      'variant' | 'size' | 'disabled'
    >,
    LabelVariantProps {
  /**
   * Shows a required indicator (red asterisk)
   */
  required?: boolean;
}

/**
 * Label component following HEN Design System standards.
 *
 * A thin wrapper around shadcn/ui label (Radix UI Label primitive) with HEN-specific
 * variants and design tokens. All styling uses tokens from tailwind.config.ts.
 *
 * @example
 * // Basic usage
 * <Label htmlFor="email">Email Address</Label>
 *
 * @example
 * // With HEN variant and size
 * <Label variant="default" size="l1" htmlFor="username">
 *   Username
 * </Label>
 *
 * @example
 * // Required field indicator
 * <Label htmlFor="password" required>
 *   Password
 * </Label>
 *
 * @example
 * // With icons (import from lucide-react)
 * import { Info } from 'lucide-react';
 * <Label htmlFor="terms">
 *   <Info /> Terms and Conditions
 * </Label>
 *
 * @example
 * // Disabled state
 * <Label htmlFor="disabled-input" disabled>
 *   Disabled Field
 * </Label>
 */
export const Label = React.forwardRef<
  React.ElementRef<typeof ShadcnLabel>,
  LabelProps
>(
  (
    { className, variant, size, required, disabled, children, ...props },
    ref,
  ) => {
    return (
      <ShadcnLabel
        className={cn(labelVariants({ variant, size, disabled }), className)}
        ref={ref}
        {...props}
      >
        {children}
        {required && (
          <span className='ml-1 text-error-500' aria-label='required'>
            *
          </span>
        )}
      </ShadcnLabel>
    );
  },
);

Label.displayName = 'Label';
